import { FunctionComponent, useEffect, useMemo, useState } from "react";
import bienveillanceIcon from "../../assets/values/bienveillance.svg";
import engagementIcon from "../../assets/values/engagement.svg";
import faireEquipeIcon from "../../assets/values/faire-equipe.svg";
import respectIcon from "../../assets/values/respect.svg";
import ThemeAnalyticsWidget from "../../components/FollowUp/ThemeAnalyticsWidget";
import ValeursPage from "../../components/ValeursPage/ValeursPage";
import { FollowUpItem, FollowUpStatus } from "../../models/FollowUp";
import { VALUE_CODES, VALUE_LABELS, ValueCode } from "../../models/OverviewDashboard";
import { ThemeStatisticsReport } from "../../models/ThemeStatistics";
import FollowUpService from "../../services/FollowUpService";
import FrontThemeStatisticsEvaluator from "../../services/FrontThemeStatisticsEvaluator";
import OrganizationService from "../../services/OrganizationService";
import { UPCARE_THEMES, UpCareThemeCode } from "../../utils/UpCareThemes";
import styles from "./SuiviPage.module.css";

const POLE_IDENTIFIER = "CHU-NANCY";
const ALL_THEME_CODES: UpCareThemeCode[] = UPCARE_THEMES.map((theme) => theme.code);

type ValueFilter = "all" | ValueCode;
type FollowUpView = "actions" | "themes";

const VALUE_PRESENTATION: Record<ValueCode, { icon: string; className: string }> = {
    bienveillance: { icon: bienveillanceIcon, className: styles.bienveillance },
    engagement: { icon: engagementIcon, className: styles.engagement },
    "faire-equipe": { icon: faireEquipeIcon, className: styles.faireEquipe },
    respect: { icon: respectIcon, className: styles.respect }
};

const STATUS_CLASS: Record<FollowUpStatus, string> = {
    request: styles.statusRequest,
    proposal: styles.statusProposal,
    scheduled: styles.statusScheduled,
    completed: styles.statusCompleted
};

const SuiviPage: FunctionComponent = () => {
    const [items, setItems] = useState<FollowUpItem[]>([]);
    const [selectedValue, setSelectedValue] = useState<ValueFilter>("all");
    const [selectedThemes, setSelectedThemes] = useState<UpCareThemeCode[]>(() => [...ALL_THEME_CODES]);
    const [selectedNature, setSelectedNature] = useState("all");
    const [selectedOrganization, setSelectedOrganization] = useState("all");
    const [activeView, setActiveView] = useState<FollowUpView>("actions");
    const [selectedAnalyticsTheme, setSelectedAnalyticsTheme] = useState<UpCareThemeCode>();
    const [themeReport, setThemeReport] = useState<ThemeStatisticsReport>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>();

    const fhirBaseUrl = process.env.REACT_APP_FHIR_URL ?? "fhir";
    const organizationService = useMemo(
        () => new OrganizationService({ fhirBaseUrl }),
        [fhirBaseUrl]
    );
    const followUpService = useMemo(
        () => new FollowUpService({ fhirBaseUrl }),
        [fhirBaseUrl]
    );
    const themeStatisticsEvaluator = useMemo(
        () => new FrontThemeStatisticsEvaluator(items),
        [items]
    );

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        setError(undefined);

        organizationService.getOrganizationByIdentifier(POLE_IDENTIFIER)
            .then(async (pole) => {
                const children = await organizationService.getChildOrganizations(pole.reference);
                const organizations = [pole, ...children];
                const organizationReferences = organizations.map((organization) => organization.reference);
                const organizationNames = Object.fromEntries(
                    organizations.map((organization) => [organization.reference, organization.name])
                );

                return followUpService.search({
                    organizationReferences,
                    organizationNames
                });
            })
            .then((result) => {
                if (mounted) setItems(result);
            })
            .catch((reason) => {
                console.error(reason);
                if (mounted) setError("Impossible de charger le suivi des actions.");
            })
            .finally(() => {
                if (mounted) setLoading(false);
            });

        return () => {
            mounted = false;
        };
    }, [followUpService, organizationService]);

    const natureOptions = useMemo(
        () => Array.from(new Set(items.map((item) => item.nature).filter(Boolean))).sort((a, b) => a.localeCompare(b, "fr")),
        [items]
    );
    const organizationOptions = useMemo(
        () => Array.from(new Set(items.map((item) => item.carriedBy).filter((value) => value && value !== "—"))).sort((a, b) => a.localeCompare(b, "fr")),
        [items]
    );

    const allThemesSelected = selectedThemes.length === ALL_THEME_CODES.length;

    const filteredItems = useMemo(() => items.filter((item) => {
        if (selectedValue !== "all" && !item.values.includes(selectedValue)) return false;
        if (selectedThemes.length === 0) return false;
        if (!allThemesSelected && (!item.themeCode || !selectedThemes.includes(item.themeCode))) return false;
        if (selectedNature !== "all" && item.nature !== selectedNature) return false;
        if (selectedOrganization !== "all" && item.carriedBy !== selectedOrganization) return false;
        return true;
    }), [items, selectedValue, selectedThemes, allThemesSelected, selectedNature, selectedOrganization]);

    useEffect(() => {
        let mounted = true;
        themeStatisticsEvaluator.evaluate({
            values: selectedValue === "all" ? undefined : [selectedValue],
            themes: allThemesSelected ? undefined : selectedThemes,
            natures: selectedNature === "all" ? undefined : [selectedNature],
            organizations: selectedOrganization === "all" ? undefined : [selectedOrganization]
        }).then((report) => {
            if (mounted) setThemeReport(report);
        });

        return () => {
            mounted = false;
        };
    }, [themeStatisticsEvaluator, selectedValue, selectedThemes, allThemesSelected, selectedNature, selectedOrganization]);

    const filtersActive = selectedValue !== "all" || !allThemesSelected ||
        selectedNature !== "all" || selectedOrganization !== "all";

    const clearFilters = () => {
        setSelectedValue("all");
        setSelectedThemes([...ALL_THEME_CODES]);
        setSelectedNature("all");
        setSelectedOrganization("all");
        setSelectedAnalyticsTheme(undefined);
    };

    const handleThemeToggle = (theme: UpCareThemeCode) => {
        setSelectedThemes((current) => current.includes(theme)
            ? current.filter((code) => code !== theme)
            : [...current, theme]
        );
    };

    const handleAllThemesToggle = () => {
        setSelectedThemes((current) => current.length === ALL_THEME_CODES.length
            ? []
            : [...ALL_THEME_CODES]
        );
    };

    const showThemeActions = (theme: UpCareThemeCode) => {
        setSelectedAnalyticsTheme(theme);
        setSelectedThemes([theme]);
        setActiveView("actions");
    };

    return (
        <ValeursPage titleKey="title.suivi" loading={false} needsLogin={true}>
            <div className={styles.page}>
                <section className={styles.card}>
                    <header className={styles.header}>
                        <div>
                            <h1>Suivi des actions</h1>
                            <p>Demandes, propositions et ateliers / formations du pôle.</p>
                        </div>
                        {!loading && !error && (
                            <span className={styles.total}>
                                {filteredItems.length}{filtersActive ? ` / ${items.length}` : ""} action{filteredItems.length > 1 ? "s" : ""}
                            </span>
                        )}
                    </header>

                    {!loading && !error && items.length > 0 && (
                        <nav className={styles.viewTabs} aria-label="Vues du suivi">
                            <button type="button" className={activeView === "actions" ? styles.activeViewTab : undefined} onClick={() => setActiveView("actions")}>Actions</button>
                            <button type="button" className={activeView === "themes" ? styles.activeViewTab : undefined} onClick={() => setActiveView("themes")}>Thématiques</button>
                        </nav>
                    )}

                    {!loading && !error && items.length > 0 && (
                        <section className={styles.filters} aria-label="Filtres du suivi">
                            <label>
                                <span>Valeur</span>
                                <select value={selectedValue} onChange={(event) => setSelectedValue(event.target.value as ValueFilter)}>
                                    <option value="all">Toutes les valeurs</option>
                                    {VALUE_CODES.map((value) => (
                                        <option key={value} value={value}>{VALUE_LABELS[value]}</option>
                                    ))}
                                </select>
                            </label>

                            <ThemeMultiSelect
                                selectedThemes={selectedThemes}
                                onToggle={handleThemeToggle}
                                onToggleAll={handleAllThemesToggle}
                            />

                            <label>
                                <span>Nature</span>
                                <select value={selectedNature} onChange={(event) => setSelectedNature(event.target.value)}>
                                    <option value="all">Toutes les natures</option>
                                    {natureOptions.map((nature) => (
                                        <option key={nature} value={nature}>{nature}</option>
                                    ))}
                                </select>
                            </label>
                            <label>
                                <span>Porté par</span>
                                <select value={selectedOrganization} onChange={(event) => setSelectedOrganization(event.target.value)}>
                                    <option value="all">Toutes les organisations</option>
                                    {organizationOptions.map((organization) => (
                                        <option key={organization} value={organization}>{organization}</option>
                                    ))}
                                </select>
                            </label>
                            {filtersActive && (
                                <button type="button" className={styles.clearFilters} onClick={clearFilters}>
                                    Réinitialiser
                                </button>
                            )}
                        </section>
                    )}

                    {loading && <p className={styles.state}>Chargement des actions…</p>}
                    {error && <p className={styles.error}>{error}</p>}

                    {!loading && !error && items.length === 0 && (
                        <p className={styles.state}>Aucune action à suivre pour le moment.</p>
                    )}

                    {!loading && !error && activeView === "actions" && items.length > 0 && filteredItems.length === 0 && (
                        <p className={styles.state}>Aucune action ne correspond aux filtres sélectionnés.</p>
                    )}

                    {!loading && !error && activeView === "actions" && filteredItems.length > 0 && (
                        <div className={styles.tableWrapper}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        <th>Statut</th>
                                        <th>Valeur</th>
                                        <th>Nature</th>
                                        <th>Thématique</th>
                                        <th>Titre</th>
                                        <th>Porté par</th>
                                        <th>Engagement</th>
                                        <th>Date</th>
                                        <th>Publication</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredItems.map((item) => (
                                        <FollowUpRow key={item.id} item={item} />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {!loading && !error && activeView === "themes" && themeReport && (
                        <ThemeAnalyticsWidget
                            report={themeReport}
                            selectedTheme={selectedAnalyticsTheme}
                            onThemeSelect={setSelectedAnalyticsTheme}
                            onShowActions={showThemeActions}
                        />
                    )}
                </section>
            </div>
        </ValeursPage>
    );
};

const ThemeMultiSelect: FunctionComponent<{
    selectedThemes: UpCareThemeCode[];
    onToggle: (theme: UpCareThemeCode) => void;
    onToggleAll: () => void;
}> = ({ selectedThemes, onToggle, onToggleAll }) => {
    const allSelected = selectedThemes.length === ALL_THEME_CODES.length;
    const summary = selectedThemes.length === 0
        ? "Aucune thématique"
        : allSelected
            ? "Toutes les thématiques"
            : selectedThemes.length === 1
                ? UPCARE_THEMES.find((theme) => theme.code === selectedThemes[0])?.display ?? "1 thématique"
                : `${selectedThemes.length} thématiques sélectionnées`;

    return (
        <div className={styles.themeFilter}>
            <span>Thématique</span>
            <details className={styles.themeMultiSelect}>
                <summary>{summary}</summary>
                <div className={styles.themeOptions}>
                    <label className={styles.selectAllTheme}>
                        <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={onToggleAll}
                        />
                        <span>Toutes les thématiques</span>
                    </label>
                    <div className={styles.themeOptionsSeparator} />
                    {UPCARE_THEMES.map((theme) => (
                        <label key={theme.code}>
                            <input
                                type="checkbox"
                                checked={selectedThemes.includes(theme.code)}
                                onChange={() => onToggle(theme.code)}
                            />
                            <span>{theme.display}</span>
                        </label>
                    ))}
                </div>
            </details>
        </div>
    );
};

const FollowUpRow: FunctionComponent<{ item: FollowUpItem }> = ({ item }) => (
    <tr>
        <td>
            <span className={`${styles.status} ${STATUS_CLASS[item.status]}`}>
                {item.statusLabel}
            </span>
        </td>
        <td>
            <div className={styles.values}>
                {item.values.length === 0 ? (
                    <span className={styles.noData}>—</span>
                ) : item.values.map((value) => {
                    const presentation = VALUE_PRESENTATION[value];
                    return (
                        <span
                            key={value}
                            className={`${styles.valueIcon} ${presentation.className}`}
                            title={VALUE_LABELS[value]}
                        >
                            <img src={presentation.icon} alt={VALUE_LABELS[value]} />
                        </span>
                    );
                })}
            </div>
        </td>
        <td>{item.nature}</td>
        <td>{item.theme ? <span className={styles.theme}>{item.theme}</span> : "—"}</td>
        <td className={styles.titleCell}>{item.title}</td>
        <td>{item.carriedBy}</td>
        <td>{formatEngagement(item.engagement)}</td>
        <td>{formatDate(item.date)}</td>
        <td>{formatDate(item.publishedAt)}</td>
    </tr>
);

const formatEngagement = (value?: number): string => {
    if (value === undefined) return "—";
    return `${value} ${value > 1 ? "personnes" : "personne"}`;
};

const formatDate = (value?: string): string => {
    if (!value) return "—";
    const date = new Date(value);
    if (!Number.isFinite(date.getTime())) return "—";

    return new Intl.DateTimeFormat("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric"
    }).format(date);
};

export default SuiviPage;
